import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";

export type SalaryStatus = "pending" | "processed" | "excluded" | "revoked" | "finalized" | "paid";

export type SalaryDetailRecord = {
  id: string;
  salaryId: string;
  title: string;
  type: string;
  code: string | null;
  value: string;
};

/** Backend types: allowance | bonus | deduction | penalty */
export function salaryDetailIsDeduction(type: string): boolean {
  const t = type.toLowerCase();
  return t === "deduction" || t === "deduct" || t === "penalty";
}

export function salaryDetailTypeBadge(type: string): { label: string; kind: "addition" | "deduction" } {
  if (salaryDetailIsDeduction(type)) {
    return { label: "Deduction", kind: "deduction" };
  }
  return { label: "Addition", kind: "addition" };
}

export type SalaryAttendanceSummary = {
  present: number;
  off: number;
  holiday: number;
  offWorked: number;
  halfDay: number;
  paidLeave: number;
  unpaidLeave: number;
  absent: number;
  totalDays: number;
  dayRate: number;
  divisorDays: number;
  effectiveBasic: number;
};

export type SalarySetupSnapshot = {
  employeeBasicSalary: number | null;
  effectiveBasic: number | null;
  attendanceSummary: SalaryAttendanceSummary | null;
};

export type SalaryRecord = {
  id: string;
  employeeId: string;
  employeeName: string;
  periodYear: number;
  periodMonth: number;
  status: SalaryStatus;
  notes: string | null;
  salaryBatchId: string | null;
  salaryBatchName: string | null;
  processedAt: string | null;
  basicSalaryTotal: string;
  grossMonthlyTotal: string;
  /** Exact calculated net (before round-to-5). */
  actualPay: string;
  /** Payable net (rounded to nearest 5). */
  netPayTotal: string;
  /** Outstanding active loan remaining balance; null when not provided */
  loanBalance: string | null;
  paymentDate: string | null;
  paidAmount: string | null;
  paidBy: string | null;
  setupSnapshot: SalarySetupSnapshot | null;
  details: SalaryDetailRecord[];
  detailsCount: number | null;
  createdAt: string;
  updatedAt: string;
};

type PaginatedMeta = {
  current_page?: number;
  last_page?: number;
  per_page?: number;
  total?: number;
};

export type SalaryBatchStatus = "draft" | "processing" | "review" | "finalized" | "paid";

export type SalaryBatchRecord = {
  salaryBatchId: string;
  batchId: string;
  name: string | null;
  periodYear: number;
  periodMonth: number;
  employeesCount: number;
  processedCount: number;
  excludedCount: number;
  paidCount: number;
  finalizedCount: number;
  grossPayTotal: string;
  deductionsTotal: string;
  netPayTotal: string;
  status: SalaryBatchStatus;
  updatedAt: string | null;
  hasIssues: boolean;
};

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapDetail(raw: Record<string, unknown>): SalaryDetailRecord {
  return {
    id: str(raw.id),
    salaryId: str(raw.salary_id),
    title: str(raw.title),
    type: str(raw.type),
    code: raw.code != null ? str(raw.code) : null,
    value: str(raw.value),
  };
}

function mapSetupSnapshot(raw: unknown): SalarySetupSnapshot | null {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null;
  const snap = raw as Record<string, unknown>;
  const summaryRaw = snap.attendance_summary;
  let attendanceSummary: SalaryAttendanceSummary | null = null;
  if (summaryRaw != null && typeof summaryRaw === "object" && !Array.isArray(summaryRaw)) {
    const s = summaryRaw as Record<string, unknown>;
    attendanceSummary = {
      present: num(s.present),
      off: num(s.off),
      holiday: num(s.holiday),
      offWorked: num(s.off_worked),
      halfDay: num(s.half_day),
      paidLeave: num(s.paid_leave),
      unpaidLeave: num(s.unpaid_leave),
      absent: num(s.absent),
      totalDays: num(s.total_days),
      dayRate: num(s.day_rate),
      divisorDays: num(s.divisor_days),
      effectiveBasic: num(s.effective_basic),
    };
  }

  return {
    employeeBasicSalary: snap.employee_basic_salary != null ? num(snap.employee_basic_salary) : null,
    effectiveBasic: snap.effective_basic != null ? num(snap.effective_basic) : null,
    attendanceSummary,
  };
}

function mapSalary(raw: Record<string, unknown>, includeDetails: boolean): SalaryRecord {
  const detailsRaw = raw.details;
  const details =
    includeDetails && Array.isArray(detailsRaw) ? detailsRaw.map((d) => mapDetail(d as Record<string, unknown>)) : [];

  const emp = raw.employee as Record<string, unknown> | undefined;
  const detailsFromApi = raw.details_count != null ? num(raw.details_count) : null;

  return {
    id: str(raw.id),
    employeeId: raw.employee_id != null ? str(raw.employee_id) : emp?.id != null ? str(emp.id) : "",
    employeeName: emp?.full_name != null ? str(emp.full_name) : "",
    periodYear: num(raw.period_year),
    periodMonth: num(raw.period_month),
    status: (() => {
      const s = str(raw.status);
      if (s === "finalized") return "finalized";
      if (s === "processed") return "processed";
      if (s === "revoked") return "revoked";
      if (s === "excluded") return "excluded";
      if (s === "paid") return "paid";
      return "pending";
    })(),
    notes: raw.notes != null ? str(raw.notes) : null,
    salaryBatchId: raw.salary_batch_id != null ? str(raw.salary_batch_id) : null,
    salaryBatchName: raw.salary_batch_name != null ? str(raw.salary_batch_name) : null,
    processedAt: raw.processed_at != null ? str(raw.processed_at) : null,
    basicSalaryTotal: str(raw.basic_salary),
    grossMonthlyTotal: str(raw.gross_monthly),
    actualPay: raw.actual_pay != null && str(raw.actual_pay) !== "" ? str(raw.actual_pay) : str(raw.net_pay),
    netPayTotal: str(raw.net_pay),
    loanBalance: raw.loan_balance != null && str(raw.loan_balance) !== "" ? str(raw.loan_balance) : null,
    paymentDate: raw.payment_date != null ? str(raw.payment_date) : null,
    paidAmount: raw.paid_amount != null ? str(raw.paid_amount) : null,
    paidBy: raw.paid_by != null ? str(raw.paid_by) : null,
    setupSnapshot: mapSetupSnapshot(raw.setup_snapshot),
    details,
    detailsCount: detailsFromApi ?? (includeDetails ? details.length : null),
    createdAt: str(raw.created_at),
    updatedAt: str(raw.updated_at),
  };
}

export function salaryProcessErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Something went wrong.";
}

export async function listAllSalaries(params?: {
  periodYear?: number;
  periodMonth?: number;
  salaryBatchId?: string;
  employeeId?: string;
}): Promise<SalaryRecord[]> {
  const all: SalaryRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const { rows, meta } = await listSalaries({ ...params, page, perPage: 100 });
    all.push(...rows);
    lastPage = meta.lastPage;
    page += 1;
  } while (page <= lastPage);
  return all;
}

export async function listSalaries(params?: {
  page?: number;
  perPage?: number;
  periodYear?: number;
  periodMonth?: number;
  salaryBatchId?: string;
  employeeId?: string;
}): Promise<{
  rows: SalaryRecord[];
  meta: { currentPage: number; lastPage: number; perPage: number; total: number };
}> {
  const response = await api.get<{
    data?: Record<string, unknown>[];
    meta?: PaginatedMeta;
  }>("/api/v1/salaries", {
    params: {
      page: params?.page,
      per_page: params?.perPage,
      period_year: params?.periodYear,
      period_month: params?.periodMonth,
      salary_batch_id: params?.salaryBatchId,
      employee_id: params?.employeeId ? Number(params.employeeId) : undefined,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};
  return {
    rows: list.map((r) => mapSalary(r as Record<string, unknown>, true)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? 20,
      total: meta.total ?? 0,
    },
  };
}

export async function listSalaryBatches(params?: {
  page?: number;
  perPage?: number;
  periodYear?: number;
  periodMonth?: number;
}): Promise<{
  rows: SalaryBatchRecord[];
  meta: { currentPage: number; lastPage: number; perPage: number; total: number };
}> {
  const response = await api.get<{
    data?: Record<string, unknown>[];
    meta?: PaginatedMeta;
  }>("/api/v1/salaries/batches", {
    params: {
      page: params?.page,
      per_page: params?.perPage,
      period_year: params?.periodYear,
      period_month: params?.periodMonth,
    },
  });
  const list = Array.isArray(response.data?.data) ? response.data.data : [];
  const meta = response.data?.meta ?? {};

  const mapRow = (raw: Record<string, unknown>): SalaryBatchRecord => {
    const st = str(raw.status);
    const batchStatus: SalaryBatchStatus =
      st === "draft" ||
      st === "processing" ||
      st === "review" ||
      st === "finalized" ||
      st === "paid"
        ? st
        : "review";

    return {
      salaryBatchId: str(raw.batch_id) || str(raw.salary_batch_id),
      batchId: str(raw.batch_id) || str(raw.salary_batch_id),
      name: raw.name != null ? str(raw.name) : null,
      periodYear: num(raw.period_year),
      periodMonth: num(raw.period_month),
      employeesCount: num(raw.employees_count),
      processedCount: num(raw.processed_count),
      excludedCount: num(raw.excluded_count),
      paidCount: num(raw.paid_count),
      finalizedCount: num(raw.finalized_count),
      grossPayTotal: str(raw.gross_pay_total),
      deductionsTotal: str(raw.deductions_total),
      netPayTotal: str(raw.net_pay_total),
      status: batchStatus,
      updatedAt: raw.updated_at != null ? str(raw.updated_at) : null,
      hasIssues: Boolean(raw.has_issues),
    };
  };

  return {
    rows: list.map((r) => mapRow(r as Record<string, unknown>)),
    meta: {
      currentPage: meta.current_page ?? 1,
      lastPage: meta.last_page ?? 1,
      perPage: meta.per_page ?? 20,
      total: meta.total ?? 0,
    },
  };
}

export async function listAllSalaryBatches(): Promise<SalaryBatchRecord[]> {
  const all: SalaryBatchRecord[] = [];
  let page = 1;
  let lastPage = 1;
  do {
    const { rows, meta } = await listSalaryBatches({ page, perPage: 100 });
    all.push(...rows);
    lastPage = meta.lastPage;
    page += 1;
  } while (page <= lastPage);
  return all;
}

export async function regenerateSalaryBatchById(payload: {
  salaryBatchId: string;
}): Promise<{ message: string; salaryBatchId: string | null; processed: number; skipped: number; errors: unknown[] }> {
  const response = await api.post<{
    message?: string;
    data?: { batch_id?: string | null; salary_batch_id?: string | null; processed?: number; skipped?: number; errors?: unknown[] };
  }>(`/api/v1/salaries/batches/${encodeURIComponent(payload.salaryBatchId)}/regenerate`);
  const data = response.data?.data;
  return {
    message: str(response.data?.message) || "Batch regenerated.",
    salaryBatchId: data?.salary_batch_id != null ? str(data.salary_batch_id) : data?.batch_id != null ? str(data.batch_id) : null,
    processed: num(data?.processed),
    skipped: num(data?.skipped),
    errors: Array.isArray(data?.errors) ? data?.errors ?? [] : [],
  };
}

export async function createSalary(payload: {
  periodYear: number;
  periodMonth: number;
  notes?: string | null;
}): Promise<{ message: string; salaries: SalaryRecord[]; periodYear: number; periodMonth: number }> {
  const response = await api.post<{
    message?: string;
    data?: { period_year?: number; period_month?: number; salaries?: Record<string, unknown>[] };
  }>("/api/v1/salaries", {
    period_year: payload.periodYear,
    period_month: payload.periodMonth,
    notes: payload.notes ?? null,
  });
  const data = response.data?.data as Record<string, unknown> | undefined;
  const salariesRaw = data?.salaries;
  const salaries = Array.isArray(salariesRaw)
    ? salariesRaw.map((s) => mapSalary(s as Record<string, unknown>, false))
    : [];
  return {
    message: str(response.data?.message) || "Created.",
    salaries,
    periodYear: num(data?.period_year),
    periodMonth: num(data?.period_month),
  };
}

export async function processSalaryBatch(payload: {
  periodYear: number;
  periodMonth: number;
  batchName?: string | null;
  employeeIds?: string[];
}): Promise<{ message: string; salaryBatchId: string | null; processed: number; skipped: number; errors: unknown[] }> {
  const response = await api.post<{
    message?: string;
    data?: { batch_id?: string | null; salary_batch_id?: string | null; processed?: number; skipped?: number; errors?: unknown[] };
  }>("/api/v1/salaries/process-batch", {
    period_year: payload.periodYear,
    period_month: payload.periodMonth,
    batch_name: payload.batchName?.trim() || null,
    employee_ids: payload.employeeIds?.map((x) => Number(x)).filter((n) => Number.isFinite(n)) ?? null,
  });
  const data = response.data?.data;
  return {
    message: str(response.data?.message) || "Batch processed.",
    salaryBatchId: data?.salary_batch_id != null ? str(data?.salary_batch_id) : data?.batch_id != null ? str(data.batch_id) : null,
    processed: num(data?.processed),
    skipped: num(data?.skipped),
    errors: Array.isArray(data?.errors) ? data?.errors ?? [] : [],
  };
}

export async function finalizeSalaryBatch(payload: {
  periodYear: number;
  periodMonth: number;
  employeeIds?: string[];
}): Promise<{ message: string; finalized: number }> {
  const response = await api.post<{
    message?: string;
    data?: { finalized?: number };
  }>("/api/v1/salaries/finalize-batch", {
    period_year: payload.periodYear,
    period_month: payload.periodMonth,
    employee_ids: payload.employeeIds?.map((x) => Number(x)).filter((n) => Number.isFinite(n)) ?? null,
  });
  const data = response.data?.data;
  return { message: str(response.data?.message) || "Finalized.", finalized: num(data?.finalized) };
}

export async function excludeSalaryBatch(payload: {
  periodYear: number;
  periodMonth: number;
  employeeIds: string[];
  reason?: string | null;
}): Promise<{ message: string; excluded: number }> {
  const response = await api.post<{
    message?: string;
    data?: { excluded?: number };
  }>("/api/v1/salaries/exclude-batch", {
    period_year: payload.periodYear,
    period_month: payload.periodMonth,
    employee_ids: payload.employeeIds.map((x) => Number(x)).filter((n) => Number.isFinite(n)),
    reason: payload.reason ?? null,
  });
  const data = response.data?.data;
  return { message: str(response.data?.message) || "Excluded.", excluded: num(data?.excluded) };
}

export async function revokeSalaryBatch(payload: {
  periodYear: number;
  periodMonth: number;
  employeeIds: string[];
  reason?: string | null;
}): Promise<{ message: string; revoked: number; skipped: number; errors: unknown[] }> {
  const response = await api.post<{
    message?: string;
    data?: { revoked?: number; skipped?: number; errors?: unknown[] };
  }>("/api/v1/salaries/revoke-batch", {
    period_year: payload.periodYear,
    period_month: payload.periodMonth,
    employee_ids: payload.employeeIds.map((x) => Number(x)).filter((n) => Number.isFinite(n)),
    reason: payload.reason ?? null,
  });
  const data = response.data?.data;
  return {
    message: str(response.data?.message) || "Revoked.",
    revoked: num(data?.revoked),
    skipped: num(data?.skipped),
    errors: Array.isArray(data?.errors) ? data?.errors ?? [] : [],
  };
}

export async function excludeSalaryBatchById(payload: {
  salaryBatchId: string;
  reason?: string | null;
}): Promise<{ message: string; excluded: number }> {
  const response = await api.post<{
    message?: string;
    data?: { excluded?: number };
  }>(`/api/v1/salaries/batches/${encodeURIComponent(payload.salaryBatchId)}/exclude`, {
    reason: payload.reason ?? null,
  });
  const data = response.data?.data;
  const n = typeof data?.excluded === "number" ? data.excluded : Number(data?.excluded);
  return { message: str(response.data?.message) || "Batch excluded.", excluded: Number.isFinite(n) ? n : 0 };
}

export async function revokeSalaryBatchById(payload: {
  salaryBatchId: string;
  reason?: string | null;
}): Promise<{ message: string; revoked: number; skipped: number; errors: unknown[] }> {
  const response = await api.post<{
    message?: string;
    data?: { revoked?: number; skipped?: number; errors?: unknown[] };
  }>(`/api/v1/salaries/batches/${encodeURIComponent(payload.salaryBatchId)}/revoke`, {
    reason: payload.reason ?? null,
  });
  const data = response.data?.data;
  return {
    message: str(response.data?.message) || "Batch revoked.",
    revoked: num(data?.revoked),
    skipped: num(data?.skipped),
    errors: Array.isArray(data?.errors) ? data?.errors ?? [] : [],
  };
}

export async function finalizeSalaryBatchById(payload: {
  salaryBatchId: string;
}): Promise<{ message: string; finalized: number }> {
  const response = await api.post<{
    message?: string;
    data?: { finalized?: number };
  }>(`/api/v1/salaries/batches/${encodeURIComponent(payload.salaryBatchId)}/finalize`);
  const data = response.data?.data;
  return { message: str(response.data?.message) || "Batch finalized.", finalized: num(data?.finalized) };
}

export async function deleteSalaryBatchById(payload: {
  salaryBatchId: string;
}): Promise<{ message: string; deleted: number }> {
  const response = await api.delete<{
    message?: string;
    data?: { deleted?: number };
  }>(`/api/v1/salaries/batches/${encodeURIComponent(payload.salaryBatchId)}`);
  const data = response.data?.data;
  return { message: str(response.data?.message) || "Batch deleted.", deleted: num(data?.deleted) };
}

export async function getSalary(id: string): Promise<SalaryRecord> {
  const response = await api.get<{ data?: Record<string, unknown> }>(`/api/v1/salaries/${encodeURIComponent(id)}`);
  const raw = response.data?.data as Record<string, unknown> | undefined;
  return mapSalary(raw ?? {}, true);
}

export async function reprocessSalary(id: string): Promise<{ salary: SalaryRecord; message: string }> {
  const response = await api.post<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/salaries/${encodeURIComponent(id)}/process`,
  );
  const raw = response.data?.data as Record<string, unknown> | undefined;
  return {
    salary: mapSalary(raw ?? {}, true),
    message: str(response.data?.message) || "Processed.",
  };
}

export async function updateSalary(
  id: string,
  payload: Partial<{
    status: SalaryStatus;
    notes: string | null;
    paymentDate: string | null;
    paidAmount: string | null;
    paidBy: string | null;
  }>,
): Promise<{ salary: SalaryRecord; message: string }> {
  const body: Record<string, unknown> = {};
  if (payload.status !== undefined) body.status = payload.status;
  if (payload.notes !== undefined) body.notes = payload.notes;
  if (payload.paymentDate !== undefined) body.payment_date = payload.paymentDate;
  if (payload.paidAmount !== undefined) body.paid_amount = payload.paidAmount;
  if (payload.paidBy !== undefined) body.paid_by = payload.paidBy;

  const response = await api.patch<{ message?: string; data?: Record<string, unknown> }>(
    `/api/v1/salaries/${encodeURIComponent(id)}`,
    body,
  );
  const raw = response.data?.data as Record<string, unknown> | undefined;
  return {
    salary: mapSalary(raw ?? {}, true),
    message: str(response.data?.message) || "Updated.",
  };
}

export async function deleteSalary(id: string): Promise<{ message: string }> {
  const response = await api.delete<{ message?: string }>(`/api/v1/salaries/${encodeURIComponent(id)}`);
  return { message: str(response.data?.message) || "Deleted." };
}

export function formatMoneyDisplay(amount: string): string {
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export async function downloadSalaryBatchPdf(batchId: string, filename?: string): Promise<void> {
  const response = await api.get<Blob>(`/api/v1/salaries/batches/${encodeURIComponent(batchId)}/pdf`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename ?? `salary-batch-${batchId}.pdf`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function downloadSalaryPdf(salaryId: string, filename?: string): Promise<void> {
  const response = await api.get<Blob>(`/api/v1/salaries/${encodeURIComponent(salaryId)}/pdf`, {
    responseType: "blob",
  });
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename ?? `salary-${salaryId}.pdf`;
  anchor.click();
  URL.revokeObjectURL(url);
}
